import apiClient from '@/lib/api-client';
import type {
  BookingPageLocation,
  BookingPagePhoto,
  BookingPageTeamMember,
  DayKey,
  OpeningHours,
  PhotoLayout,
  TimeRange,
} from '@/pages/public/booking-page/types';

export interface BookingPageSettings {
  tagline: string;
  show_hours: boolean;
  hours_source: 'team' | 'custom';
  custom_hours: Record<DayKey, TimeRange[]>;
  show_address: boolean;
  show_map: boolean;
  show_directions: boolean;
  show_online: boolean;
  team_mode: 'auto' | 'show' | 'hide';
  show_phone: boolean;
  embed: { allowed_origins: string[] };
}

export interface BookingPageAdmin {
  settings: BookingPageSettings;
  photos: BookingPagePhoto[];
  layout: PhotoLayout;
  limits: { max_photos: number; max_photo_bytes: number };
  counts: { services_active: number; services_total: number; providers_active: number };
  organization: {
    name: string;
    subdomain: string;
    logo_url: string | null;
    description: string | null;
    address: string | null;
    phone: string | null;
    has_coordinates: boolean;
    public_locales: string[];
  };
  preview_source: {
    team: BookingPageTeamMember[];
    about: string | null;
    phone: string | null;
    email: string | null;
    team_hours: OpeningHours;
    location: BookingPageLocation | null;
  };
}

const unwrap = <T,>(response: { data: { data: T } }) => response.data.data;

export const bookingPageApi = {
  get: () => apiClient.get('/v1/admin/booking_page').then(unwrap<BookingPageAdmin>),
  update: (settings: Partial<BookingPageSettings>) =>
    apiClient.put('/v1/admin/booking_page', { booking_page: settings }).then(unwrap<BookingPageAdmin>),
  uploadPhoto: (file: File) => {
    const form = new FormData();
    form.append('image', file);
    return apiClient
      .post('/v1/admin/booking_page/photos', form, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(unwrap<BookingPagePhoto>);
  },
  reorderPhotos: (ids: string[]) => apiClient.patch('/v1/admin/booking_page/photos/reorder', { ids }).then(unwrap<BookingPagePhoto[]>),
  deletePhoto: (id: string) => apiClient.delete(`/v1/admin/booking_page/photos/${id}`),
};

// First error message from a Rails render_error response.
export function apiErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { error?: string; errors?: unknown } } })?.response?.data;
  const errors = data?.errors;
  if (Array.isArray(errors) && errors.length) return String(errors[0]);
  if (errors && typeof errors === 'object') {
    const first = Object.values(errors as Record<string, string>)[0];
    if (first) return String(first);
  }
  return data?.error || fallback;
}
